/**
 * SmartEWM Playbook (placeholder)
 * -----------------------------------------------------------------------------
 * Phase-1 placeholder following the same pattern as the SmartEvaluate /
 * SmartImprove / SmartBid playbooks. Captures the operational-intelligence
 * usage guidance for the SmartEWM Operational Assistant. No backend logic.
 */

const LETTERS = ["1", "2", "3", "4", "5", "6"];

export default function SmartEwmPlaybookPage() {
  return (
    <div data-testid="smartewm-playbook-page" className="max-w-4xl">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold mb-1">
          SmartEWM Playbook
        </p>
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
          Guidelines &amp; Notes
        </h1>
        <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">
          How to use the SmartEWM Operational Assistant for operational
          intelligence across warehouse execution. AI prompts live under
          SmartEWM → AI Analysis Prompts.
        </p>
      </div>

      <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-sm px-4 py-3 mb-6 text-[12px] text-[#1E3A8A] leading-relaxed">
        This playbook is an initial placeholder. Detailed operational-intelligence
        guidance (taxonomy, KPI interpretation, exception handling) will be added
        in a later phase.
      </div>

      <Section n="1" title="What SmartEWM Does">
        <Bullets items={[
          "Provides an operational intelligence assistant over SAP EWM warehouse execution data.",
          "Surfaces operational issues, KPIs, and recommended actions for supervisors and consultants.",
          "Complements SmartEvaluate (strategy) and SmartImprove (transformation) with day-to-day execution insight.",
        ]} />
      </Section>

      <Section n="2" title="Running the Operational Assistant">
        <Bullets items={[
          "Ask operational questions in natural language; the assistant grounds answers in the connected warehouse data.",
          "Use the assistant for triage and prioritisation, not as a system of record.",
          "Always validate high-impact actions against the live SAP EWM system before execution.",
        ]} />
      </Section>

      <Section n="3" title="Interpreting Results">
        <Bullets items={[
          "Treat assistant output as decision support — confirm with operational data before acting.",
          "Watch for data freshness: insights are only as current as the latest sync.",
          "Escalate anomalies that conflict with floor reality for human review.",
        ]} />
      </Section>

      <Section n="4" title="Governance & Safety">
        <Bullets items={[
          "The assistant prompt is centrally managed under SmartEWM → AI Analysis Prompts and versioned by prompt key.",
          "Only one prompt version per key is Active at a time.",
          "Do not embed credentials or client-identifying data in prompts.",
        ]} />
      </Section>

      <Section n="5" title="Common Pitfalls">
        <Bullets items={[
          "Acting on stale data without confirming the last sync time.",
          "Treating directional insight as an exact operational guarantee.",
          "Bypassing standard SAP EWM controls based solely on assistant output.",
        ]} />
      </Section>

      <Section n="6" title="Coming Soon">
        <Bullets items={[
          "Hierarchical operational intelligence taxonomy dashboards.",
          "Deeper KPI drill-downs and exception workflows.",
        ]} />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
function Section({ n, title, children }) {
  if (!LETTERS.includes(n)) n = "•";
  return (
    <section
      data-testid={`smartewm-playbook-section-${n}`}
      className="mb-5 bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-sm bg-[#025979] text-white text-[10px] font-bold">
          {n}
        </span>
        <h2
          className="text-sm font-semibold text-[#111827] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h2>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-[12px] text-[#1F2937] leading-relaxed">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}
